import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  LocationProvider,
  CreateProviderRequest,
  UpdateProviderRequest,
  ProviderFilters,
  ProviderListResponse,
  ProviderStatistics,
  ProviderTypesResponse,
} from '../types/provider';
import {
  ProviderTemplatesResponse,
  ProviderTemplateResponse,
  DefaultConfigResponse,
  ValidateConfigResponse,
} from '../types/providerTemplate';
import {
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryListResponse,
  CategoryResponse,
} from '../types/category';
import { sortCategoriesByName } from '../utils/sortCategoriesByName';
import type {
  CityTransportApiConfig,
  PreviewFetchRequest,
  PreviewFetchResponse,
  PreviewLinesRequest,
  PreviewLinesResponse,
  GtfsImportPayload,
  GtfsImportResponse,
  OgcFeaturesImportPayload,
  GtfsImportRow,
  ConfiguredTransportCity,
  GtfsBrowseResponse,
} from '../types/cityTransportApi';
import { config } from '../config/environment';
import {
  clearAuthSession,
  getStoredToken,
  hasValidAuthSession,
  isTokenExpired,
  parseAuthToken,
  redirectToLogin,
} from '../utils/authSession';

class ApiService {
  private api: AxiosInstance;
  private sessionRedirecting = false;

  constructor() {
    this.api = axios.create({
      baseURL: config.apiUrl,
    });

    this.api.interceptors.request.use((requestConfig) => {
      const token = getStoredToken();
      const requestUrl = String(requestConfig.url ?? '');
      const isLoginRequest = requestUrl.includes('/login');

      if (!token) {
        return requestConfig;
      }

      if (isTokenExpired(token)) {
        clearAuthSession();
        if (!isLoginRequest) {
          this.handleSessionExpired();
          return Promise.reject(new axios.CanceledError('Session expirée'));
        }
        return requestConfig;
      }

      if (!isLoginRequest) {
        requestConfig.headers = requestConfig.headers ?? {};
        requestConfig.headers['X-Auth-Token'] = token;
      }
      return requestConfig;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const hadToken = Boolean(error.config?.headers?.['X-Auth-Token']);
        const url = String(error.config?.url ?? '');
        const isAuthRequest = url.includes('/login') || url.includes('/register');
        const isStaticAsset =
          url.includes('/uploads/')
          || /\.(svg|png|jpe?g|gif|webp)(\?|$)/i.test(url);

        if (!isStaticAsset && !isAuthRequest && hadToken && (status === 401 || status === 403)) {
          this.handleSessionExpired();
        }

        return Promise.reject(error);
      }
    );
  }

  private handleSessionExpired(): void {
    if (this.sessionRedirecting) return;
    this.sessionRedirecting = true;
    redirectToLogin();
  }

  // ===== AUTHENTIFICATION =====
  
  async login(email: string, password: string): Promise<{ token: string; user: any }> {
    const response: AxiosResponse<{ token: string; user: any }> = await this.api.post('/login', {
      email,
      password,
    });
    
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data;
  }

  async logout(): Promise<void> {
    clearAuthSession();
  }

  isAuthenticated(): boolean {
    return hasValidAuthSession();
  }

  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /** Vérifie auprès de l’API que la session est encore valide (rôle + compte actif). */
  async verifySession(): Promise<any> {
    const response: AxiosResponse<{ user: any }> = await this.api.get('/profile');
    const profileUser = response.data?.user ?? {};
    const storedUser = this.getCurrentUser() ?? {};
    const tokenPayload = parseAuthToken(getStoredToken() ?? '') ?? {};

    const roles =
      (Array.isArray(profileUser.roles) && profileUser.roles.length > 0
        ? profileUser.roles
        : null)
      ?? (Array.isArray(storedUser.roles) && storedUser.roles.length > 0 ? storedUser.roles : null)
      ?? (Array.isArray(tokenPayload.roles) && tokenPayload.roles.length > 0 ? tokenPayload.roles : null)
      ?? [];

    const user = {
      ...storedUser,
      ...profileUser,
      roles,
      first_name: profileUser.first_name ?? profileUser.firstName ?? storedUser.first_name,
      last_name: profileUser.last_name ?? profileUser.lastName ?? storedUser.last_name,
    };

    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }

  userHasSuperAdminRole(user: any): boolean {
    const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
    if (roles.includes('ROLE_SUPER_ADMIN')) {
      return true;
    }
    const tokenPayload = parseAuthToken(getStoredToken() ?? '');
    const tokenRoles = tokenPayload?.roles;
    return Array.isArray(tokenRoles) && tokenRoles.includes('ROLE_SUPER_ADMIN');
  }

  // ===== PRESTATAIRES DE LIEU =====
  
  /**
   * Récupère la liste des partenaires
   */
  async getProviders(filters?: ProviderFilters): Promise<ProviderListResponse> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const response: AxiosResponse<ProviderListResponse> = await this.api.get(`/providers?${params.toString()}`);
    return response.data;
  }

  /**
   * Récupère un partenaire par ID
   */
  async getProvider(id: number): Promise<LocationProvider> {
    const response: AxiosResponse<LocationProvider> = await this.api.get(`/providers/${id}`);
    return response.data;
  }

  /**
   * Crée un nouveau partenaire
   */
  async createProvider(provider: CreateProviderRequest): Promise<LocationProvider> {
    const response: AxiosResponse<LocationProvider> = await this.api.post('/providers', provider);
    return response.data;
  }

  /**
   * Met à jour un partenaire
   */
  async updateProvider(id: number, updates: UpdateProviderRequest): Promise<LocationProvider> {
    const response: AxiosResponse<LocationProvider> = await this.api.put(`/providers/${id}`, updates);
    return response.data;
  }

  /** Cadrage logo page partenaire privée (zoom / position dans le cercle). */
  async updateProviderPrivatePageLogoLayout(
    id: number,
    layout: { scale: number; offsetX: number; offsetY: number },
    currentSettings?: Record<string, unknown> | null
  ): Promise<LocationProvider> {
    const settings = { ...(currentSettings || {}), private_page_logo_layout: layout };
    // Colonne dédiée + copie dans settings (secours si API prod partielle).
    return this.updateProvider(id, {
      private_page_logo_layout: layout,
      settings: settings as LocationProvider['settings'],
    });
  }

  /** Crée ou met à jour l’accès administrateur du dashboard partenaire. */
  async updateProviderAdminAccess(
    providerId: number,
    data: {
      email?: string;
      password?: string;
      first_name?: string;
      last_name?: string;
      send_access_email?: boolean;
    }
  ): Promise<{
    provider: LocationProvider;
    message?: string;
    email_sent?: boolean;
    email_error?: string | null;
    manual_reset_url?: string | null;
  }> {
    const response = await this.api.put(`/providers/${providerId}/admin-access`, data);
    return response.data;
  }

  /**
   * Met à jour le statut d'un partenaire
   */
  async updateProviderStatus(id: number, status: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.patch(`/providers/${id}/status`, { status });
    return response.data;
  }

  /** Ordre du menu « Partenaires » sur le site public */
  async updateProvidersPublicMenuOrder(
    orders: Array<{ id: number; public_menu_sort_order: number }>
  ): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.put(
      '/providers/public-menu-order',
      { orders }
    );
    return response.data;
  }

  /**
   * Supprime un partenaire
   */
  async deleteProvider(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/providers/${id}`);
    return response.data;
  }

  /**
   * Bilan avant suppression partenaire (super-admin)
   */
  async getProviderDeletionPreview(providerId: number): Promise<{
    warning: string;
    counts: Record<string, number | boolean>;
  }> {
    const response = await this.api.get(`/providers/${providerId}/delete-preview`);
    return response.data;
  }

  /**
   * Récupère les statistiques des partenaires
   */
  async getProviderStatistics(): Promise<{ statistics: ProviderStatistics; top_providers: LocationProvider[] }> {
    const response: AxiosResponse<{ statistics: ProviderStatistics; top_providers: LocationProvider[] }> = await this.api.get('/providers/statistics');
    return response.data;
  }

  /**
   * Récupère les types de partenaires disponibles
   */
  async getProviderTypes(): Promise<ProviderTypesResponse> {
    const response: AxiosResponse<ProviderTypesResponse> = await this.api.get('/providers/types');
    return response.data;
  }

  /**
   * Types de partenaires (entité provider_type en base) — pour liaison provider_type_id
   */
  async getProviderTypesFromEntity(): Promise<{
    provider_types: Array<{
      id: number;
      name: string;
      slug: string;
      icon?: string | null;
      icon_svg_filename?: string | null;
      icon_svg_url?: string | null;
      item_name?: string | null;
      default_attributes?: Record<string, { type?: string; label?: string; options?: string[]; required?: boolean }>;
      annual_event?: boolean;
      is_active?: boolean;
    }>;
    total: number;
  }> {
    const response = await this.api.get('/provider-types');
    return response.data;
  }

  /**
   * Picto SVG du type de partenaire (dépôt dans uploads/pictos, validation côté API).
   */
  async uploadProviderTypePictoSvg(providerTypeId: number, file: File): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append('picto', file);
    return this.api.post(`/provider-types/${providerTypeId}/picto-svg`, formData);
  }

  async deleteProviderTypePictoSvg(providerTypeId: number): Promise<AxiosResponse> {
    return this.api.delete(`/provider-types/${providerTypeId}/picto-svg`);
  }

  /**
   * Récupère les partenaires actifs
   */
  async getActiveProviders(): Promise<LocationProvider[]> {
    const response = await this.getProviders({ status: 'active', limit: 1000 });
    return response.providers;
  }

  /**
   * Récupère les partenaires en attente d'approbation
   */
  async getPendingProviders(): Promise<LocationProvider[]> {
    const response = await this.getProviders({ status: 'pending_approval', limit: 1000 });
    return response.providers;
  }

  /**
   * Recherche des partenaires
   */
  async searchProviders(query: string, type?: string, status?: string): Promise<LocationProvider[]> {
    const response = await this.getProviders({ search: query, type: type as any, status: status as any, limit: 1000 });
    return response.providers;
  }

  // ===== TEMPLATES DE PRESTATAIRES =====
  
  /**
   * Récupère tous les templates de partenaires
   */
  async getProviderTemplates(): Promise<ProviderTemplatesResponse> {
    const response: AxiosResponse<ProviderTemplatesResponse> = await this.api.get('/provider-templates');
    return response.data;
  }

  // ===== TARIFS LIVRAISON (ADMIN) =====

  async getDeliveryPricingAdmin(): Promise<{
    colissimo_amount_cents: number;
    chronopost_enabled: boolean;
    chronopost_services: Record<
      string,
      {
        delivery_mode: string;
        label: string;
        amount_cents: number;
        amount_eur: number;
        estimated_cost_cents: number | null;
        estimated_cost_eur: number | null;
        margin_cents: number | null;
        margin_eur: number | null;
        margin_percent: number | null;
        product_code: string;
        requires_relay: boolean;
        uses_home_contract: boolean;
        enabled: boolean;
      }
    >;
    chronopost_relais_13_amount_cents: number;
    chronopost_shop2shop_amount_cents: number;
    chronopost_relais_europe_amount_cents: number;
    chronopost_relais_dom_amount_cents: number;
    chronopost_home_13_amount_cents: number;
    chronopost_home_10_amount_cents: number;
    chronopost_home_18_amount_cents: number;
    mondial_relay_enabled: boolean;
    mondial_relay_amount_cents: number;
    ups_enabled?: boolean;
    ups_home_amount_cents?: number;
    ups_access_point_amount_cents?: number;
    updated_at: string | null;
    chronopost_product_codes?: Record<string, string>;
    carrier_logos?: {
      colissimo: string | null;
      chronopost: string | null;
      mondial_relay: string | null;
      ups: string | null;
    };
    colissimo_logo?: string | null;
    chronopost_logo?: string | null;
    mondial_relay_logo?: string | null;
    ups_logo?: string | null;
    rate_brackets?: Record<
      string,
      Array<{
        id: string;
        weight_min_kg: number;
        weight_max_kg: number;
        carrier_price_cents: number;
        carrier_price_eur?: number;
        sherlook_fee_type: 'percent' | 'fixed';
        sherlook_fee_value: number;
        sherlook_fee_cents?: number;
        sherlook_fee_eur?: number;
        total_cents?: number;
        total_eur?: number;
        category_ids: number[];
        type_values?: string[];
      }>
    >;
  }> {
    const response = await this.api.get('/admin/delivery-pricing');
    return response.data;
  }

  async updateDeliveryPricingAdmin(payload: {
    colissimo_amount_cents?: number;
    chronopost_enabled?: boolean;
    chronopost_services?: Record<
      string,
      {
        enabled?: boolean;
        amount_cents?: number;
        estimated_cost_cents?: number | null;
      }
    >;
    chronopost_relais_13_amount_cents?: number;
    chronopost_shop2shop_amount_cents?: number;
    chronopost_relais_europe_amount_cents?: number;
    chronopost_relais_dom_amount_cents?: number;
    chronopost_home_13_amount_cents?: number;
    chronopost_home_10_amount_cents?: number;
    chronopost_home_18_amount_cents?: number;
    mondial_relay_enabled?: boolean;
    mondial_relay_amount_cents?: number;
    ups_enabled?: boolean;
    ups_home_amount_cents?: number;
    ups_access_point_amount_cents?: number;
    rate_brackets?: Record<
      string,
      Array<{
        id?: string;
        weight_min_kg: number;
        weight_max_kg: number;
        carrier_price_cents: number;
        sherlook_fee_type: 'percent' | 'fixed';
        sherlook_fee_value: number;
        category_ids: number[];
        type_values?: string[];
      }>
    >;
  }): Promise<{
    colissimo_amount_cents: number;
    chronopost_enabled: boolean;
    chronopost_services: Record<
      string,
      {
        delivery_mode: string;
        label: string;
        amount_cents: number;
        amount_eur: number;
        estimated_cost_cents: number | null;
        estimated_cost_eur: number | null;
        margin_cents: number | null;
        margin_eur: number | null;
        margin_percent: number | null;
        product_code: string;
        requires_relay: boolean;
        uses_home_contract: boolean;
        enabled: boolean;
      }
    >;
    chronopost_relais_13_amount_cents: number;
    chronopost_shop2shop_amount_cents: number;
    chronopost_relais_europe_amount_cents: number;
    chronopost_relais_dom_amount_cents: number;
    chronopost_home_13_amount_cents: number;
    chronopost_home_10_amount_cents: number;
    chronopost_home_18_amount_cents: number;
    mondial_relay_enabled: boolean;
    mondial_relay_amount_cents: number;
    ups_enabled?: boolean;
    ups_home_amount_cents?: number;
    ups_access_point_amount_cents?: number;
    updated_at: string | null;
    rate_brackets?: Record<string, unknown[]>;
  }> {
    const response = await this.api.patch('/admin/delivery-pricing', payload);
    return response.data;
  }

  async uploadDeliveryCarrierLogo(
    carrier: 'colissimo' | 'chronopost' | 'mondial_relay' | 'ups',
    formData: FormData
  ): Promise<{
    carrier: string;
    logo: string;
    logo_url: string | null;
    carrier_logos: {
      colissimo: string | null;
      chronopost: string | null;
      mondial_relay: string | null;
      ups: string | null;
    };
  }> {
    const response = await this.api.post(`/admin/delivery-pricing/carrier-logo/${carrier}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteDeliveryCarrierLogo(
    carrier: 'colissimo' | 'chronopost' | 'mondial_relay' | 'ups'
  ): Promise<{
    carrier: string;
    logo: string | null;
    logo_url: string | null;
    carrier_logos: {
      colissimo: string | null;
      chronopost: string | null;
      mondial_relay: string | null;
      ups: string | null;
    };
  }> {
    const response = await this.api.delete(`/admin/delivery-pricing/carrier-logo/${carrier}`);
    return response.data;
  }

  // ===== LABO QA (ADMIN) =====

  async getQaLabStatus(): Promise<{
    seeded: boolean;
    password: string;
    mail_redirect_email: string | null;
    accounts: Record<string, { label: string; email: string; id: number | null; exists: boolean }>;
    provider: {
      id: number;
      name: string;
      slug: string | null;
      place_storage_mode: string;
      place_storage_mode_override: string | null;
      private_announcements: boolean;
      visible_on_frontend: boolean;
      is_qa_lab: boolean;
    } | null;
    scenario: {
      lost_announcement_id: number | null;
      found_announcement_id: number | null;
      found_objects_count: number;
      matches_count: number;
    };
    urls: { frontend: string; provider_dashboard: string };
    how_to: string[];
    message?: string;
    matches_created_on_seed?: number;
  }> {
    const response = await this.api.get('/admin/qa-lab');
    return response.data;
  }

  async seedQaLab(): Promise<{ message?: string }> {
    const response = await this.api.post('/admin/qa-lab/seed', { run_matching: true });
    return response.data;
  }

  async resetQaLab(): Promise<{ message?: string }> {
    const response = await this.api.post('/admin/qa-lab/reset', { run_matching: true });
    return response.data;
  }

  async setQaLabStorageMode(mode: 'central' | 'combined' | 'split'): Promise<{ message?: string }> {
    const response = await this.api.post('/admin/qa-lab/storage-mode', { mode });
    return response.data;
  }

  async setQaLabMailRedirect(email: string | null): Promise<{ message?: string; mail_redirect_email?: string | null }> {
    const response = await this.api.post('/admin/qa-lab/mail-redirect', { email });
    return response.data;
  }

  async getMessagingSettingsAdmin(): Promise<{
    loser_message_templates: string[];
    admin_activity_notify_email: string | null;
    notify_particulier_match: boolean;
    notify_particulier_message: boolean;
    updated_at: string | null;
  }> {
    const response = await this.api.get('/admin/messaging-settings');
    return response.data;
  }

  async updateMessagingSettingsAdmin(payload: {
    loser_message_templates?: string[] | null;
    admin_activity_notify_email?: string | null;
    notify_particulier_match?: boolean;
    notify_particulier_message?: boolean;
  }): Promise<{
    loser_message_templates: string[];
    admin_activity_notify_email: string | null;
    notify_particulier_match: boolean;
    notify_particulier_message: boolean;
    updated_at: string | null;
  }> {
    const response = await this.api.patch('/admin/messaging-settings', payload);
    return response.data;
  }

  async testMessagingAdminNotify(): Promise<{ success: boolean; message?: string; to?: string }> {
    const response = await this.api.post('/admin/messaging-settings/test-notify');
    return response.data;
  }

  // ===== STRIPE / LIVRAISON COLISSIMO (ADMIN) =====

  async getStripeSettings(): Promise<{
    success: boolean;
    settings: {
      mode: 'test' | 'live';
      test: {
        configured: boolean;
        public_key_hint: string | null;
        secret_key_hint: string | null;
        webhook_secret_hint: string | null;
      };
      live: {
        configured: boolean;
        public_key_hint: string | null;
        secret_key_hint: string | null;
        webhook_secret_hint: string | null;
      };
    };
  }> {
    const response = await this.api.get('/admin/stripe/settings');
    return response.data;
  }

  async updateStripeSettings(payload: { mode: 'test' | 'live' }): Promise<{
    success: boolean;
    message: string;
    settings: {
      mode: 'test' | 'live';
      test: { configured: boolean; public_key_hint: string | null; secret_key_hint: string | null; webhook_secret_hint: string | null };
      live: { configured: boolean; public_key_hint: string | null; secret_key_hint: string | null; webhook_secret_hint: string | null };
    };
  }> {
    const response = await this.api.put('/admin/stripe/settings', payload);
    return response.data;
  }

  async createStripeRefund(payload: {
    conversation_id: number;
    amount_eur?: number | null;
    reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
  }): Promise<{
    success: boolean;
    message: string;
    refund: {
      refund_id: string;
      status: string;
      amount_eur: number;
      amount_cents: number;
      stripe_session_id: string;
      conversation_id: number;
      payment_message_id: number;
      refund_status: 'partial' | 'full';
    };
  }> {
    const response = await this.api.post('/admin/stripe/refunds', payload);
    return response.data;
  }

  async getDeliveryTransactions(filters?: {
    page?: number;
    limit?: number;
    tracking?: string;
    person?: string;
    status?: string;
  }): Promise<{
    transactions: Array<{
      conversation_id: number;
      message_id: number;
      created_at: string;
      stage: 'offer' | 'checkout_pending' | 'paid' | 'failed' | 'unknown';
      has_offer: boolean;
      has_payment: boolean;
      pending_checkout_count: number;
      stripe_session_id: string | null;
      amount_eur: number | null;
      paid_amount_eur?: number | null;
      proposed_amount_eur?: number | null;
      refund_status: 'none' | 'partial' | 'full';
      refunded_amount_eur: number | null;
      tracking: string | null;
      is_returned?: boolean | null;
      returned_at?: string | null;
      return_label?: string | null;
      is_delivered?: boolean | null;
      delivered_at?: string | null;
      delivered_label?: string | null;
      parcel_status?: string | null;
      etiquette_url: string | null;
      payer: {
        id?: number;
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string | null;
        address?: string | null;
        postal_code?: string | null;
        city?: string | null;
      };
      finder: { id?: number; first_name?: string; last_name?: string; email?: string };
      announcement: { id?: number; title?: string; type?: string };
      announcement_thumbnail_url: string | null;
      found_object: { id: number; status: string; reference_code?: string | null } | null;
      shipping?: {
        mode?: 'home' | 'pickup_point' | null;
        address?: string | null;
        pickup_point?: string | null;
        phone?: string | null;
      } | null;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.tracking) params.append('tracking', filters.tracking);
    if (filters?.person) params.append('person', filters.person);
    if (filters?.status) params.append('status', filters.status);

    const response = await this.api.get(`/admin/delivery/transactions?${params.toString()}`);
    return response.data;
  }

  async getStripePayments(filters?: { page?: number; limit?: number }): Promise<{
    payments: Array<{
      message_id: number;
      created_at: string;
      stripe_session_id: string | null;
      amount_eur: number | null;
      tracking: string | null;
      etiquette_url: string | null;
      status: 'success' | 'failed';
      payer: { id?: number; first_name?: string; last_name?: string; email?: string };
      finder: { id?: number; first_name?: string; last_name?: string; email?: string };
      announcement: { id?: number; title?: string; type?: string };
      announcement_thumbnail_url: string | null;
      conversation_id?: number;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response: AxiosResponse<any> = await this.api.get(`/admin/stripe/payments?${params.toString()}`);
    return response.data;
  }

  async getColissimoTracking(params: { tracking: string }): Promise<{
    tracking: string;
    steps: unknown[] | null;
    raw: unknown;
    is_returned?: boolean | null;
    returned_at?: string | null;
    return_label?: string | null;
  }> {
    const response: AxiosResponse<{
      tracking: string;
      steps: unknown[] | null;
      raw: unknown;
      is_returned?: boolean | null;
      returned_at?: string | null;
      return_label?: string | null;
    }> = await this.api.post('/delivery/colissimo/tracking', { tracking: params.tracking });

    return response.data;
  }

  async getDeliveryConversationStatus(conversationId: number): Promise<{
    delivery: {
      conversation_id: number;
      has_offer: boolean;
      has_payment: boolean;
      pending_checkout_count: number;
      offer_message_ids: number[];
      payment_message_ids: number[];
      found_object: { id: number; status: string } | null;
      announcement_active: boolean;
    };
  }> {
    const response = await this.api.get(`/admin/delivery/conversations/${conversationId}/status`);
    return response.data;
  }

  async updateDeliveryProposedAmount(
    conversationId: number,
    amountEur: number
  ): Promise<{
    message: string;
    result: {
      conversation_id: number;
      proposed_amount_eur: number;
      proposed_amount_cents: number;
      pending_checkouts_cleared: number;
      transaction: any;
    };
  }> {
    const response = await this.api.patch(`/admin/delivery/conversations/${conversationId}/proposed-amount`, {
      amount_eur: amountEur,
    });
    return response.data;
  }

  async resetDeliveryConversation(
    conversationId: number,
    scope: 'full' | 'payment_only' = 'full'
  ): Promise<{
    status: string;
    result: {
      conversation_id: number;
      scope: string;
      deleted: { offer: number; payment: number; pending: number };
      found_object_status_before: string | null;
      found_object_status_after: string | null;
    };
    delivery: {
      conversation_id: number;
      has_offer: boolean;
      has_payment: boolean;
      pending_checkout_count: number;
      offer_message_ids: number[];
      payment_message_ids: number[];
      found_object: { id: number; status: string } | null;
      announcement_active: boolean;
    };
  }> {
    const response = await this.api.post(`/admin/delivery/conversations/${conversationId}/reset`, { scope });
    return response.data;
  }

  async regenerateDeliveryLabel(conversationId: number): Promise<{
    status: string;
    labelUrl?: string | null;
    trackingNumber?: string | null;
    qrCodeData?: string | null;
    qrCodeUrl?: string | null;
    messageId?: number;
    error?: string;
  }> {
    const response = await this.api.post(
      `/admin/delivery/conversations/${conversationId}/regenerate-label`
    );
    return response.data;
  }

  /**
   * Récupère un template pour un type de partenaire spécifique
   */
  async getProviderTemplate(type: string): Promise<ProviderTemplateResponse> {
    const response: AxiosResponse<ProviderTemplateResponse> = await this.api.get(`/provider-templates/${type}`);
    return response.data;
  }

  /**
   * Récupère la configuration par défaut pour un type de partenaire
   */
  async getDefaultConfig(type: string): Promise<DefaultConfigResponse> {
    const response: AxiosResponse<DefaultConfigResponse> = await this.api.get(`/provider-templates/${type}/default-config`);
    return response.data;
  }

  /**
   * Valide une configuration selon le template
   */
  async validateConfig(type: string, config: any): Promise<ValidateConfigResponse> {
    const response: AxiosResponse<ValidateConfigResponse> = await this.api.post(`/provider-templates/${type}/validate`, { config });
    return response.data;
  }

  // ===== MÉTHODES GÉNÉRIQUES =====
  
  /**
   * Méthode GET générique
   */
  async get(url: string): Promise<AxiosResponse> {
    return this.api.get(url);
  }

  /**
   * Méthode POST générique
   */
  async post(url: string, data?: any): Promise<AxiosResponse> {
    return this.api.post(url, data);
  }

  /**
   * Méthode PUT générique
   */
  async put(url: string, data?: any): Promise<AxiosResponse> {
    return this.api.put(url, data);
  }

  /**
   * Méthode DELETE générique
   */
  async delete(url: string): Promise<AxiosResponse> {
    return this.api.delete(url);
  }

  /**
   * Méthode PATCH générique
   */
  async patch(url: string, data?: any): Promise<AxiosResponse> {
    return this.api.patch(url, data);
  }

  // ===== CATEGORIES =====

  /**
   * Récupérer la liste des catégories
   */
  async getCategories(): Promise<AxiosResponse<CategoryListResponse>> {
    const response = await this.api.get<CategoryListResponse>('/categories');
    if (response.data?.categories) {
      response.data.categories = sortCategoriesByName(response.data.categories);
    }
    return response;
  }

  /**
   * Récupérer une catégorie par ID
   */
  async getCategory(id: number): Promise<AxiosResponse<CategoryResponse>> {
    const response = await this.api.get<CategoryResponse | Category>(`/categories/${id}`);
    const raw = response.data;
    const category =
      raw && typeof raw === 'object' && 'category' in raw && raw.category
        ? raw.category
        : (raw as Category);
    return {
      ...response,
      data: { category },
    };
  }

  /**
   * Créer une nouvelle catégorie
   */
  async createCategory(data: CreateCategoryRequest): Promise<AxiosResponse<CategoryResponse>> {
    return this.api.post('/categories', data);
  }

  /**
   * Mettre à jour une catégorie
   */
  async updateCategory(id: number, data: UpdateCategoryRequest): Promise<AxiosResponse<CategoryResponse>> {
    return this.api.put(`/categories/${id}`, data);
  }

  /**
   * Supprimer une catégorie
   */
  async deleteCategory(id: number): Promise<AxiosResponse> {
    return this.api.delete(`/categories/${id}`);
  }

  /**
   * Image par défaut de catégorie (JPEG/PNG/WebP)
   */
  async uploadCategoryDefaultImage(id: number, file: File): Promise<AxiosResponse<{ category: Category }>> {
    const formData = new FormData();
    formData.append('image', file);
    // Ne pas fixer Content-Type : axios ajoute multipart/form-data + boundary (sinon le serveur ne reçoit pas le fichier)
    return this.api.post(`/categories/${id}/default-image`, formData);
  }

  async deleteCategoryDefaultImage(id: number): Promise<AxiosResponse<{ category: Category }>> {
    return this.api.delete(`/categories/${id}/default-image`);
  }

  // ===== USERS =====

  /**
   * Récupérer la liste des utilisateurs
   */
  async getUsers(filters?: any): Promise<AxiosResponse> {
    return this.api.get('/users', { params: filters });
  }

  /**
   * Exporte les e-mails utilisateurs inscrits sur une période (séparés par des virgules).
   */
  async exportUserEmails(params: {
    date_from?: string;
    date_to?: string;
    status?: string;
    never_logged_in?: boolean;
  }): Promise<AxiosResponse<{
    count: number;
    emails: string;
    date_from: string | null;
    date_to: string | null;
    never_logged_in?: boolean;
  }>> {
    return this.api.get('/users/export-emails', {
      params: {
        ...params,
        never_logged_in: params.never_logged_in ? 1 : undefined,
      },
    });
  }

  /**
   * Récupérer un utilisateur par ID
   */
  async getUser(id: number): Promise<AxiosResponse> {
    return this.api.get(`/users/${id}`);
  }

  /**
   * Basculer le statut d'un utilisateur
   */
  async toggleUserStatus(userId: number, reason?: string): Promise<AxiosResponse> {
    return this.api.patch(`/users/${userId}/toggle-status`, reason ? { reason } : {});
  }

  /**
   * Marquer un utilisateur comme email vérifié (super-admin, sans envoi de mail)
   */
  async verifyUser(userId: number): Promise<AxiosResponse> {
    return this.api.patch(`/users/${userId}/verify`);
  }

  /**
   * Renvoyer l'email de vérification (super-admin)
   */
  async resendUserVerificationEmail(userId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.api.post(`/users/${userId}/resend-verification`);
  }

  /**
   * Supprimer un utilisateur
   */
  async deleteUser(userId: number): Promise<AxiosResponse> {
    return this.api.delete(`/users/${userId}`);
  }

  /**
   * Bilan avant suppression utilisateur (super-admin)
   */
  async getUserDeletionPreview(userId: number): Promise<{
    warning: string;
    counts: Record<string, number | boolean>;
  }> {
    const response = await this.api.get(`/users/${userId}/delete-preview`);
    return response.data;
  }

  /**
   * Récupérer les conversations d'un utilisateur
   */
  async getUserConversations(userId: number, filters?: any): Promise<AxiosResponse> {
    return this.api.get(`/moderation/users/${userId}/conversations`, { params: filters });
  }

  /**
   * Récupérer les annonces d'un utilisateur
   */
  async getUserAnnouncements(userId: number, filters?: any): Promise<AxiosResponse> {
    return this.api.get(`/users/${userId}/announcements`, { params: filters });
  }

  // ===== CONVERSATIONS =====

  /**
   * Récupérer les détails d'une conversation
   */
  async getConversationDetails(conversationId: number): Promise<AxiosResponse> {
    return this.api.get(`/moderation/conversations/${conversationId}`);
  }

  /**
   * Récupérer les messages d'une conversation
   */
  async getConversationMessages(conversationId: number, filters?: any): Promise<AxiosResponse> {
    return this.api.get(`/moderation/conversations/${conversationId}/messages`, { params: filters });
  }

  /**
   * Modérer un message
   */
  async moderateMessage(messageId: number, action: any): Promise<AxiosResponse> {
    return this.api.post(`/moderation/messages/${messageId}/moderate`, action);
  }

  /**
   * Intervenir dans une conversation
   */
  async interveneInConversation(conversationId: number, message: any): Promise<AxiosResponse> {
    return this.api.post(`/moderation/conversations/${conversationId}/intervene`, message);
  }

  async getImageModerationAlerts(filters?: {
    page?: number;
    limit?: number;
    in_trash?: 0 | 1;
  }): Promise<AxiosResponse> {
    return this.api.get('/admin/moderation/image-alerts', { params: filters });
  }

  async reviewImageModerationAlert(
    alertId: number,
    payload: { action: 'approve' | 'reject' | 'unapprove'; reason?: string }
  ): Promise<AxiosResponse> {
    return this.api.patch(`/admin/moderation/image-alerts/${alertId}/review`, payload);
  }

  async trashImageModerationAlert(alertId: number): Promise<AxiosResponse> {
    return this.api.patch(`/admin/moderation/image-alerts/${alertId}/trash`, {});
  }

  async restoreImageModerationAlert(alertId: number): Promise<AxiosResponse> {
    return this.api.patch(`/admin/moderation/image-alerts/${alertId}/restore`, {});
  }

  // ===== PROVIDER USERS =====

  /**
   * Récupérer les utilisateurs d'un partenaire
   */
  async getProviderUsers(filters?: any): Promise<AxiosResponse> {
    // Convertir providerId en provider_id pour le backend
    const backendFilters = { ...filters };
    if (backendFilters.providerId !== undefined) {
      backendFilters.provider_id = backendFilters.providerId;
      delete backendFilters.providerId;
    }
    return this.api.get('/provider-users', { params: backendFilters });
  }

  /**
   * Créer un utilisateur partenaire
   */
  async createProviderUser(data: any): Promise<AxiosResponse> {
    const payload = { ...data };
    if (payload.providerId !== undefined) {
      payload.provider_id = payload.providerId;
      delete payload.providerId;
    }
    if (payload.sendAccessEmail !== undefined) {
      payload.send_access_email = payload.sendAccessEmail;
      delete payload.sendAccessEmail;
    }
    return this.api.post('/provider-users', payload);
  }

  /**
   * Mettre à jour un utilisateur partenaire
   */
  async updateProviderUser(id: number, data: any): Promise<AxiosResponse> {
    const payload = { ...data };
    if (payload.providerId !== undefined) {
      payload.provider_id = payload.providerId;
      delete payload.providerId;
    }
    return this.api.put(`/provider-users/${id}`, payload);
  }

  /**
   * Détacher un utilisateur d'un partenaire (sans supprimer le compte)
   */
  async detachProviderUser(userId: number, providerId: number): Promise<AxiosResponse> {
    try {
      return await this.api.post(`/providers/${providerId}/users/${userId}/detach`);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        throw error;
      }
      return this.api.post(`/provider-users/${userId}/detach`, {
        provider_id: providerId,
      });
    }
  }

  /**
   * Supprimer un utilisateur partenaire
   */
  async deleteProviderUser(userId: number): Promise<AxiosResponse> {
    return this.api.delete(`/provider-users/${userId}`);
  }

  /**
   * Activer un utilisateur partenaire
   */
  async activateProviderUser(userId: number): Promise<AxiosResponse> {
    return this.api.post(`/provider-users/${userId}/activate`);
  }

  /**
   * Désactiver un utilisateur partenaire
   */
  async deactivateProviderUser(userId: number): Promise<AxiosResponse> {
    return this.api.post(`/provider-users/${userId}/deactivate`);
  }

  /**
   * Générer un mot de passe pour un utilisateur partenaire
   */
  async generateProviderUserPassword(): Promise<AxiosResponse> {
    return this.api.post('/provider-users/generate-password');
  }

  /**
   * Associer un utilisateur à un partenaire
   */
  async associateProviderUser(userId: number, providerId: number, sendAccessEmail = true): Promise<AxiosResponse> {
    return this.api.post(`/provider-users/associate`, {
      user_id: userId,
      provider_id: providerId,
      send_access_email: sendAccessEmail,
    });
  }

  // ===== LOGO UPLOAD =====

  /**
   * Uploader un logo de partenaire
   */
  async uploadProviderLogo(providerId: number, formData: FormData): Promise<AxiosResponse> {
    return this.api.post(`/providers/${providerId}/logo`, formData);
  }

  /**
   * Supprimer le logo d'un partenaire
   */
  async deleteProviderLogo(providerId: number): Promise<AxiosResponse> {
    return this.api.delete(`/providers/${providerId}/logo`);
  }

  // ===== ICÔNES PERSONNALISÉES =====

  /**
   * Uploader une icône SVG personnalisée
   */
  async uploadIcon(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('icon', file);
    
    const response = await this.api.post('/icons/upload', formData);
    return response.data;
  }

  /**
   * Récupérer la liste des icônes personnalisées
   */
  async getIcons(): Promise<any> {
    const response = await this.api.get('/icons/list');
    return response.data;
  }

  /**
   * Supprimer une icône personnalisée
   */
  async deleteIcon(filename: string): Promise<any> {
    const safe = encodeURIComponent(filename);
    const response = await this.api.delete(`/icons/delete/${safe}`);
    return response.data;
  }

  // ===== TRANSPORTS PAR VILLE (Open Data) — ROLE_SUPER_ADMIN =====

  async getCityTransportApis(): Promise<CityTransportApiConfig[]> {
    const response = await this.api.get('/admin/city-transport-apis');
    return response.data;
  }

  async getCityTransportApi(id: number): Promise<CityTransportApiConfig> {
    const response = await this.api.get(`/admin/city-transport-apis/${id}`);
    return response.data;
  }

  async createCityTransportApi(payload: CityTransportApiConfig): Promise<CityTransportApiConfig> {
    const response = await this.api.post('/admin/city-transport-apis', payload);
    return response.data;
  }

  async updateCityTransportApi(
    id: number,
    payload: Partial<CityTransportApiConfig>
  ): Promise<CityTransportApiConfig> {
    const response = await this.api.put(`/admin/city-transport-apis/${id}`, payload);
    return response.data;
  }

  async deleteCityTransportApi(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/admin/city-transport-apis/${id}`);
    return response.data;
  }

  async previewCityTransportFetch(payload: PreviewFetchRequest): Promise<PreviewFetchResponse> {
    const response = await this.api.post('/admin/city-transport-apis/preview-fetch', payload);
    return response.data;
  }

  async previewCityTransportLines(payload: PreviewLinesRequest): Promise<PreviewLinesResponse> {
    const response = await this.api.post('/admin/city-transport-apis/preview-lines', payload);
    return response.data;
  }

  async getCityTransportGtfsImports(): Promise<GtfsImportRow[]> {
    const response = await this.api.get('/admin/city-transport-apis/gtfs-imports');
    return response.data;
  }

  async importCityTransportGtfs(payload: GtfsImportPayload): Promise<GtfsImportResponse> {
    const response = await this.api.post('/admin/city-transport-apis/gtfs-import', payload);
    return response.data;
  }

  async importCityTransportOgcFeatures(payload: OgcFeaturesImportPayload): Promise<GtfsImportResponse> {
    const response = await this.api.post('/admin/city-transport-apis/ogc-features-import', payload);
    return response.data;
  }

  async deleteCityTransportGtfsImport(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/admin/city-transport-apis/gtfs-imports/${id}`);
    return response.data;
  }

  async getConfiguredTransportCities(): Promise<{ cities: ConfiguredTransportCity[] }> {
    const response = await this.api.get('/admin/city-transport-apis/configured-cities');
    return response.data;
  }

  async browseGtfsDatasets(params: {
    q?: string;
    city?: string;
    region?: string;
    department?: string;
    commune?: string;
    page?: number;
    page_size?: number;
    tag?: string;
    source?: 'datagouv' | 'transport';
    hide_covered?: boolean;
  }): Promise<GtfsBrowseResponse> {
    const sp = new URLSearchParams();
    if (params.q) sp.append('q', params.q);
    if (params.city) sp.append('city', params.city);
    if (params.region) sp.append('region', params.region);
    if (params.department) sp.append('department', params.department);
    if (params.commune) sp.append('commune', params.commune);
    if (params.page != null) sp.append('page', String(params.page));
    if (params.page_size != null) sp.append('page_size', String(params.page_size));
    if (params.tag) sp.append('tag', params.tag);
    if (params.source) sp.append('source', params.source);
    if (params.hide_covered) sp.append('hide_covered', '1');
    const response = await this.api.get(`/admin/city-transport-apis/gtfs-browse?${sp.toString()}`);
    return response.data;
  }

  async getTransportPanFacets(params?: {
    region?: string;
    department?: string;
    commune_q?: string;
  }): Promise<import('../types/cityTransportApi').TransportPanFacets> {
    const sp = new URLSearchParams();
    if (params?.region) sp.append('region', params.region);
    if (params?.department) sp.append('department', params.department);
    if (params?.commune_q) sp.append('commune_q', params.commune_q);
    const qs = sp.toString();
    const response = await this.api.get(
      `/admin/city-transport-apis/transport-facets${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  // ===== HOME REVIEWS (Google-like) =====
  async getGoogleReviewsConfig(): Promise<{
    success: boolean;
    config: {
      rating: number;
      reviews: Array<{
        id: string;
        first_name: string;
        last_name?: string;
        user_name?: string;
        avatar_url?: string;
        stars: number;
        text: string;
        relative_date: string;
      }>;
    };
  }> {
    const response = await this.api.get('/google-reviews-config');
    return response.data;
  }

  async updateGoogleReviewsConfig(payload: {
    rating: number;
    reviews: Array<{
      id: string;
      first_name: string;
      last_name?: string;
      user_name?: string;
      avatar_url?: string;
      stars: number;
      text: string;
      relative_date: string;
    }>;
  }): Promise<{ success: boolean; message: string; config: any }> {
    const response = await this.api.put('/google-reviews-config', payload);
    return response.data;
  }

  async getHomeAnnouncementThemeConfig(): Promise<{
    success: boolean;
    config: {
      lost_color: string;
      found_color: string;
    };
  }> {
    const response = await this.api.get('/home-announcement-theme');
    return response.data;
  }

  async updateHomeAnnouncementThemeConfig(payload: {
    lost_color: string;
    found_color: string;
  }): Promise<{ success: boolean; message: string; config: { lost_color: string; found_color: string } }> {
    const response = await this.api.put('/home-announcement-theme', payload);
    return response.data;
  }

  async getFrontendUiConfig(): Promise<{
    success: boolean;
    config: {
      locale_switcher_hidden: boolean;
      email_verification_disabled: boolean;
    };
  }> {
    const response = await this.api.get('/frontend-ui-config');
    return response.data;
  }

  async updateFrontendUiConfig(payload: {
    locale_switcher_hidden: boolean;
    email_verification_disabled: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    config: {
      locale_switcher_hidden: boolean;
      email_verification_disabled: boolean;
    };
  }> {
    const response = await this.api.put('/frontend-ui-config', payload);
    return response.data;
  }

  async getHomeSplashScreenConfig(): Promise<{
    success: boolean;
    config: Record<string, unknown>;
    partners: Array<Record<string, unknown>>;
  }> {
    const response = await this.api.get('/home-splash-screen');
    return response.data;
  }

  async updateHomeSplashScreenConfig(payload: Record<string, unknown>): Promise<{
    success: boolean;
    message: string;
    config: Record<string, unknown>;
    partners: Array<Record<string, unknown>>;
  }> {
    const response = await this.api.put('/home-splash-screen', payload);
    return response.data;
  }

  async uploadHomeSplashScreenIcon(file: File): Promise<{
    success: boolean;
    icon: { filename: string; url: string };
  }> {
    const formData = new FormData();
    formData.append('icon', file);
    const response = await this.api.post('/home-splash-screen/icon-upload', formData);
    return response.data;
  }

  async uploadHomeSplashScreenCover(file: File): Promise<{
    success: boolean;
    image: { filename: string; url: string };
  }> {
    const formData = new FormData();
    formData.append('cover', file);
    const response = await this.api.post('/home-splash-screen/icon-upload', formData);
    return response.data;
  }

  // ===== HOME TICKER (annonces fictives accueil) =====
  async getHomeTickerAnnouncements(): Promise<{ announcements: import('../types/homeTickerAnnouncement').HomeTickerAnnouncement[] }> {
    const response = await this.api.get('/home-ticker-announcements');
    return response.data;
  }

  async createHomeTickerAnnouncement(
    data: import('../types/homeTickerAnnouncement').HomeTickerAnnouncementPayload
  ): Promise<{ message: string; announcement: import('../types/homeTickerAnnouncement').HomeTickerAnnouncement }> {
    const response = await this.api.post('/home-ticker-announcements', data);
    return response.data;
  }

  async updateHomeTickerAnnouncement(
    id: number,
    data: Partial<import('../types/homeTickerAnnouncement').HomeTickerAnnouncementPayload>
  ): Promise<{ message: string; announcement: import('../types/homeTickerAnnouncement').HomeTickerAnnouncement }> {
    const response = await this.api.put(`/home-ticker-announcements/${id}`, data);
    return response.data;
  }

  async deleteHomeTickerAnnouncement(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/home-ticker-announcements/${id}`);
    return response.data;
  }

  async uploadHomeTickerAnnouncementImage(
    id: number,
    file: File
  ): Promise<{ message: string; announcement: import('../types/homeTickerAnnouncement').HomeTickerAnnouncement }> {
    const formData = new FormData();
    formData.append('image', file);
    return this.api.post(`/home-ticker-announcements/${id}/image`, formData).then((r) => r.data);
  }

  async deleteHomeTickerAnnouncementImage(
    id: number
  ): Promise<{ message: string; announcement: import('../types/homeTickerAnnouncement').HomeTickerAnnouncement }> {
    const response = await this.api.delete(`/home-ticker-announcements/${id}/image`);
    return response.data;
  }

  // ===== REDIRECTIONS URL (ADMIN) =====
  async getUrlRedirectsAdmin(): Promise<{ redirects: import('../types/urlRedirect').UrlRedirect[] }> {
    const response = await this.api.get('/admin/url-redirects');
    return response.data;
  }

  async createUrlRedirectAdmin(
    data: import('../types/urlRedirect').UrlRedirectPayload
  ): Promise<{ message: string; redirect: import('../types/urlRedirect').UrlRedirect }> {
    const response = await this.api.post('/admin/url-redirects', data);
    return response.data;
  }

  async updateUrlRedirectAdmin(
    id: number,
    data: Partial<import('../types/urlRedirect').UrlRedirectPayload>
  ): Promise<{ message: string; redirect: import('../types/urlRedirect').UrlRedirect }> {
    const response = await this.api.put(`/admin/url-redirects/${id}`, data);
    return response.data;
  }

  async deleteUrlRedirectAdmin(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/admin/url-redirects/${id}`);
    return response.data;
  }

  async getMaintenanceMode(): Promise<{
    success: boolean;
    config: { enabled: boolean; message: string };
  }> {
    const response = await this.api.get('/maintenance-mode');
    return response.data;
  }

  async getMaintenanceModeAdmin(): Promise<{
    success: boolean;
    config: { enabled: boolean; message: string; allowed_ips: string[] };
  }> {
    const response = await this.api.get('/admin/maintenance-mode');
    return response.data;
  }

  async updateMaintenanceModeAdmin(payload: {
    enabled?: boolean;
    allowed_ips?: string[];
  }): Promise<{
    success: boolean;
    config: { enabled: boolean; message: string; allowed_ips: string[] };
  }> {
    const response = await this.api.patch('/admin/maintenance-mode', payload);
    return response.data;
  }

  async updateMaintenanceMode(payload: { enabled: boolean; message?: string }, destroyToken: string): Promise<{
    success: boolean;
    config: { enabled: boolean; message: string };
  }> {
    const response = await this.api.put('/maintenance-mode', payload, {
      headers: { 'X-Destroy-Token': destroyToken },
    });
    return response.data;
  }

  /** Matchings (super-admin uniquement, côté API). */
  async getAdminMatchings(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Array<{
      id: number;
      status: string;
      match_score: string | null;
      match_reason: string | null;
      created_at: string | null;
      source_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
      target_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
      target_found_object: { id: number; title: string; reference_code: string | null } | null;
    }>;
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  }> {
    const sp = new URLSearchParams();
    if (params.search) sp.append('search', params.search);
    if (params.page != null) sp.append('page', String(params.page));
    if (params.limit != null) sp.append('limit', String(params.limit));
    const response = await this.api.get(`/admin/matchings?${sp.toString()}`);
    return response.data;
  }

  async postAdminMatchingsRecalculate(): Promise<{ queued: boolean; message: string }> {
    const response = await this.api.post('/admin/matchings/recalculate');
    return response.data;
  }

  async getAdminMatching(id: number): Promise<{
    data: {
      id: number;
      status: string;
      match_score: string | null;
      match_reason: string | null;
      created_at: string | null;
      source_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
      target_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
      target_found_object: { id: number; title: string; reference_code: string | null } | null;
    };
  }> {
    const response = await this.api.get(`/admin/matchings/${id}`);
    return response.data;
  }

  async postAdminMatchingForceEmail(id: number): Promise<{
    ok: boolean;
    message: string;
    sent_to: string[];
    sent_count: number;
  }> {
    const response = await this.api.post(`/admin/matchings/${id}/force-email`);
    return response.data;
  }

  async getAdminAnnouncements(params: import('../types/announcementAdmin').AdminAnnouncementsFilters = {}): Promise<
    import('../types/announcementAdmin').AdminAnnouncementsListResponse
  > {
    const sp = new URLSearchParams();
    if (params.search) sp.append('search', params.search);
    if (params.user_id != null) sp.append('user_id', String(params.user_id));
    if (params.type) sp.append('type', params.type);
    if (params.status) sp.append('status', params.status);
    if (params.date_from) sp.append('date_from', params.date_from);
    if (params.date_to) sp.append('date_to', params.date_to);
    if (params.provider_id != null) sp.append('provider_id', String(params.provider_id));
    if (params.only_individual) sp.append('only_individual', '1');
    if (params.category_id != null) sp.append('category_id', String(params.category_id));
    if (params.page != null) sp.append('page', String(params.page));
    if (params.limit != null) sp.append('limit', String(params.limit));
    const response = await this.api.get(`/admin/announcements?${sp.toString()}`);
    return response.data;
  }

  async getAnnouncementCreationsChart(
    params: import('../types/announcementCreationsChart').AnnouncementCreationsChartParams,
  ): Promise<import('../types/announcementCreationsChart').AnnouncementCreationsChartResponse> {
    const sp = new URLSearchParams();
    sp.append('granularity', params.granularity);
    sp.append('date_from', params.date_from);
    sp.append('date_to', params.date_to);
    if (params.city) sp.append('city', params.city);
    const response = await this.api.get(`/admin/announcements/creations-chart?${sp.toString()}`);
    return response.data;
  }

  async getAnnouncementCreationCities(): Promise<
    import('../types/announcementCreationsChart').AnnouncementCreationCitiesResponse
  > {
    const response = await this.api.get('/admin/announcements/creation-cities');
    return response.data;
  }

  async getDashboardSummary(
    params: import('../types/dashboardStats').DashboardChartFilters,
  ): Promise<import('../types/dashboardStats').DashboardSummary> {
    const sp = new URLSearchParams();
    sp.append('date_from', params.date_from);
    sp.append('date_to', params.date_to);
    if (params.city) sp.append('city', params.city);
    const response = await this.api.get(`/admin/dashboard/summary?${sp.toString()}`);
    return response.data;
  }

  async getDashboardMatchingsChart(
    params: import('../types/dashboardStats').DashboardChartFilters,
  ): Promise<import('../types/dashboardStats').DashboardMatchingsChartResponse> {
    const sp = new URLSearchParams();
    sp.append('granularity', params.granularity);
    sp.append('date_from', params.date_from);
    sp.append('date_to', params.date_to);
    if (params.city) sp.append('city', params.city);
    const response = await this.api.get(`/admin/dashboard/matchings-chart?${sp.toString()}`);
    return response.data;
  }

  async getDashboardDeliveriesChart(
    params: import('../types/dashboardStats').DashboardChartFilters,
  ): Promise<import('../types/dashboardStats').DashboardDeliveriesChartResponse> {
    const sp = new URLSearchParams();
    sp.append('granularity', params.granularity);
    sp.append('date_from', params.date_from);
    sp.append('date_to', params.date_to);
    if (params.city) sp.append('city', params.city);
    const response = await this.api.get(`/admin/dashboard/deliveries-chart?${sp.toString()}`);
    return response.data;
  }

  async getDashboardCities(): Promise<{ cities: import('../types/dashboardStats').DashboardCityOption[] }> {
    const response = await this.api.get('/admin/dashboard/cities');
    return response.data;
  }

  async getAdminAnnouncement(id: number): Promise<{ data: import('../types/announcementAdmin').AdminAnnouncementDetail }> {
    const response = await this.api.get(`/admin/announcements/${id}`);
    return response.data;
  }

  async patchAdminAnnouncementStatus(
    id: number,
    status: 'active' | 'closed',
  ): Promise<{ message: string; data: import('../types/announcementAdmin').AdminAnnouncementDetail }> {
    const response = await this.api.patch(`/admin/announcements/${id}/status`, { status });
    return response.data;
  }

  async updateAdminAnnouncement(
    id: number,
    payload: import('../types/announcementAdmin').AdminAnnouncementUpdatePayload,
  ): Promise<{ message: string; data: import('../types/announcementAdmin').AdminAnnouncementDetail }> {
    const response = await this.api.patch(`/admin/announcements/${id}`, payload);
    return response.data;
  }

  async deleteAdminAnnouncement(id: number): Promise<{ message: string; id: number }> {
    const response = await this.api.delete(`/admin/announcements/${id}`);
    return response.data;
  }

  /** Suppression forcée objet trouvé partenaire (conversations / images / miroir FO-CONTACT). */
  async deleteAdminFoundObject(id: number): Promise<{ message: string; id: number }> {
    const response = await this.api.delete(`/admin/found-objects/${id}`);
    return response.data;
  }

  async recalculateAnnouncementMatches(id: number): Promise<{ message: string; matches_created: number }> {
    const response = await this.api.post(`/admin/announcements/${id}/recalculate-matches`);
    return response.data;
  }

  async getMaintenancePreview(): Promise<{
    backup_notice: string;
    unreferenced_announcement_image_files: { count: number; examples: string[] };
    orphan_announcements: number;
    orphan_found_objects: number;
    orphan_conversations: number;
    orphan_messages: number;
  }> {
    const response = await this.api.get('/admin/maintenance/preview');
    return response.data;
  }

  async postMaintenanceUnreferencedImages(): Promise<{ deleted_files: number; skipped: string[] }> {
    const response = await this.api.post('/admin/maintenance/unreferenced-images');
    return response.data;
  }

  async postMaintenanceOrphanRecords(): Promise<{
    deleted_messages: number;
    deleted_conversations: number;
    purged_announcements: number;
    removed_found_objects: number;
  }> {
    const response = await this.api.post('/admin/maintenance/orphan-records');
    return response.data;
  }

  async getPhpInfoHtml(): Promise<string> {
    const response = await this.api.get('/admin/phpinfo', { responseType: 'text' });
    return typeof response.data === 'string' ? response.data : String(response.data ?? '');
  }

  async getSymfonyLogs(params?: {
    file?: string;
    lines?: number;
    search?: string;
    date?: string;
    hour?: number | '';
  }): Promise<{
    files: Array<{ name: string; size: number; modified_at: string }>;
    file?: string;
    lines?: string[];
    entries?: Array<{
      datetime: string | null;
      datetime_label: string | null;
      timezone: string | null;
      level: string;
      channel: string;
      category: string;
      severity: string;
      message: string;
      message_secondary: string | null;
      exception: string | null;
      exception_chain: string | null;
      http_code: number | null;
      http_label: string | null;
      file: string | null;
      file_path: string | null;
      line: number | null;
      is_critical_alert: boolean;
      raw: string;
    }>;
    total_lines_returned?: number;
    truncated?: boolean;
    search?: string | null;
    date?: string | null;
    hour?: number | null;
  }> {
    const response = await this.api.get('/admin/logs', { params });
    return response.data;
  }

  async getEmailTestTemplates(): Promise<{
    default_recipient: string;
    templates: Array<{ id: string; label: string; description: string }>;
  }> {
    const response = await this.api.get('/admin/email-tests/templates');
    return response.data;
  }

  async sendEmailTest(
    templateId: string,
    recipient?: string
  ): Promise<{
    ok: boolean;
    template_id: string;
    template_label: string;
    sent_to: string;
    message: string;
  }> {
    const response = await this.api.post('/admin/email-tests/send', {
      template_id: templateId,
      recipient: recipient || undefined,
    });
    return response.data;
  }

  async getSatisfactionEmailProviders(): Promise<{ providers: Array<{ id: number; name: string }> }> {
    const response = await this.api.get('/admin/satisfaction-emails/providers');
    return response.data;
  }

  async previewSatisfactionEmailAudience(payload: {
    audience: 'delivery_payers' | 'provider_clients' | 'handover_claimants';
    location_provider_id?: number;
    force?: boolean;
  }): Promise<{
    audience: string;
    location_provider_id: number | null;
    force?: boolean;
    total: number;
    deliverable: number;
    skipped_no_email: number;
    skipped_already_sent?: number;
    skipped_banned?: number;
    sample: Array<{ id: number; email: string; first_name: string | null; last_name: string | null }>;
  }> {
    const response = await this.api.post('/admin/satisfaction-emails/preview', payload);
    return response.data;
  }

  async sendSatisfactionEmailTest(payload: {
    recipient: string;
    subject: string;
    body: string;
    cta_url?: string;
    cta_label?: string;
    location_provider_id?: number;
  }): Promise<{ ok: boolean; sent_to: string; message: string }> {
    const response = await this.api.post('/admin/satisfaction-emails/send-test', payload);
    return response.data;
  }

  async sendSatisfactionEmailCampaign(payload: {
    audience: 'delivery_payers' | 'provider_clients' | 'handover_claimants';
    location_provider_id?: number;
    subject: string;
    body: string;
    cta_url?: string;
    cta_label?: string;
    limit?: number;
    force?: boolean;
  }): Promise<{
    ok: boolean;
    sent: number;
    skipped: number;
    skipped_already_sent?: number;
    skipped_banned?: number;
    errors: number;
    message: string;
    error_samples: string[];
  }> {
    const response = await this.api.post('/admin/satisfaction-emails/send', payload);
    return response.data;
  }

  async getSatisfactionEmailBans(params?: {
    q?: string;
    limit?: number;
  }): Promise<{
    bans: Array<{
      id: number;
      email: string;
      display_name: string | null;
      reason: string | null;
      created_at: string;
    }>;
  }> {
    const response = await this.api.get('/admin/satisfaction-emails/bans', { params });
    return response.data;
  }

  async searchSatisfactionEmailBanCandidates(params: {
    q: string;
    limit?: number;
    location_provider_id?: number;
  }): Promise<{
    candidates: Array<{
      id: number;
      email: string;
      first_name: string | null;
      last_name: string | null;
      banned: boolean;
      source?: string;
      reference_code?: string | null;
      found_object_id?: number | null;
    }>;
  }> {
    const response = await this.api.get('/admin/satisfaction-emails/ban-candidates', { params });
    return response.data;
  }

  async banSatisfactionEmail(payload: {
    email: string;
    display_name?: string;
    reason?: string;
  }): Promise<{
    id: number;
    email: string;
    display_name: string | null;
    reason: string | null;
    created_at: string;
    created: boolean;
  }> {
    const response = await this.api.post('/admin/satisfaction-emails/bans', payload);
    return response.data;
  }

  async unbanSatisfactionEmail(id: number): Promise<{ ok: boolean }> {
    const response = await this.api.delete(`/admin/satisfaction-emails/bans/${id}`);
    return response.data;
  }

  async getMonitoringSnapshot(params?: {
    since?: string;
    provider_id?: number;
    period?: import('../types/monitoring').MonitoringPeriod;
  }): Promise<import('../types/monitoring').MonitoringSnapshot> {
    const response = await this.api.get('/admin/monitoring/snapshot', {
      params: {
        since: params?.since,
        provider_id: params?.provider_id,
        period: params?.period,
      },
    });
    return response.data;
  }

  async getMonitoringAnalyticsRealtime(): Promise<import('../types/monitoring').MonitoringAnalyticsRealtime> {
    const response = await this.api.get('/admin/monitoring/analytics-realtime');
    return response.data;
  }

  async getPerformanceMonitoringConfig(): Promise<import('../types/performanceMonitoring').PerformanceMonitoringConfig> {
    const response = await this.api.get('/admin/monitoring/performance/config');
    return response.data;
  }

  async getPerformanceDashboard(params?: {
    from?: string;
    to?: string;
  }): Promise<import('../types/performanceMonitoring').PerformanceDashboard> {
    const response = await this.api.get('/admin/monitoring/performance/dashboard', { params });
    return response.data;
  }

  async getPerformanceLogs(params?: {
    page?: number;
    limit?: number;
    from?: string;
    to?: string;
    route?: string;
    user_id?: number;
    status_code?: number;
    endpoint?: string;
    q?: string;
    has_exception?: string;
  }): Promise<{ items: import('../types/performanceMonitoring').PerformanceLogRow[]; total: number; page: number; limit: number }> {
    const response = await this.api.get('/admin/monitoring/performance/logs', { params });
    return response.data;
  }

  async getPerformanceAlerts(params?: { open?: boolean; limit?: number }): Promise<{
    items: import('../types/performanceMonitoring').PerformanceAlert[];
    total: number;
  }> {
    const response = await this.api.get('/admin/monitoring/performance/alerts', {
      params: { open: params?.open === false ? '0' : '1', limit: params?.limit },
    });
    return response.data;
  }

  async acknowledgePerformanceAlert(id: number): Promise<void> {
    await this.api.patch(`/admin/monitoring/performance/alerts/${id}/acknowledge`);
  }

  getPerformanceLogsExportUrl(params?: Record<string, string | number | undefined>): string {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') sp.append(k, String(v));
      });
    }
    sp.append('format', 'csv');
    const base = this.api.defaults.baseURL ?? '';
    return `${base.replace(/\/$/, '')}/admin/monitoring/performance/logs/export?${sp.toString()}`;
  }

  async downloadPerformanceLogsExport(params?: Record<string, string | number | undefined>): Promise<void> {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') sp.append(k, String(v));
      });
    }
    sp.append('format', 'csv');
    const response = await this.api.get(`/admin/monitoring/performance/logs/export?${sp.toString()}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

export const apiService = new ApiService();
export default apiService;

